import React, { useEffect, useRef, useState } from "react";

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  removeTolerance?: number; // 0-255 distance threshold
  imageWidth?: number;
  imageHeight?: number;
}

function colorDistance(
  r1: number,
  g1: number,
  b1: number,
  r2: number,
  g2: number,
  b2: number,
) {
  const dr = r1 - r2;
  const dg = g1 - g2;
  const db = b1 - b2;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

export default function TransparentLogo({
  src,
  removeTolerance = 60,
  alt,
  imageWidth = 200,
  imageHeight = 50,
  ...rest
}: Props) {
  const [processedSrc, setProcessedSrc] = useState<string>(src); // Start with original src
  const imgRef = useRef<HTMLImageElement | null>(null);
  const mounted = useRef(true);
  const processed = useRef(false);

  // Set fetchpriority via DOM to avoid React warning
  useEffect(() => {
    if (imgRef.current) {
      imgRef.current.setAttribute("fetchpriority", "high");
    }
  }, []);

  useEffect(() => {
    // Don't re-process if already done
    if (processed.current) return;

    mounted.current = true;

    // Defer processing to after initial render (non-blocking)
    const processImage = () => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = src;

      const onLoad = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width || 256;
          canvas.height = img.naturalHeight || img.height || 256;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("No canvas context");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          // Determine background color by sampling corners
          const samplePixels: number[][] = [];
          const w = canvas.width - 1;
          const h = canvas.height - 1;
          const getPixel = (x: number, y: number) => {
            const idx = (y * canvas.width + x) * 4;
            return [data[idx], data[idx + 1], data[idx + 2]];
          };
          samplePixels.push(getPixel(0, 0));
          samplePixels.push(getPixel(w, 0));
          samplePixels.push(getPixel(0, h));
          samplePixels.push(getPixel(w, h));

          const avg = samplePixels
            .reduce(
              (acc, p) => [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]],
              [0, 0, 0],
            )
            .map((v) => Math.round(v / samplePixels.length)) as number[];

          const bgR = avg[0],
            bgG = avg[1],
            bgB = avg[2];

          // Iterate and make similar pixels transparent
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            const dist = colorDistance(r, g, b, bgR, bgG, bgB);
            if (a > 10 && dist <= removeTolerance) {
              data[i + 3] = 0;
            }
          }

          ctx.putImageData(imageData, 0, 0);
          const out = canvas.toDataURL("image/png");
          if (mounted.current) {
            setProcessedSrc(out);
            processed.current = true;
          }
        } catch (e) {
          // If processing fails (CORS or other), keep original src
          processed.current = true;
        }
      };

      const onError = () => {
        processed.current = true;
      };

      img.onload = onLoad;
      img.onerror = onError;
    };

    // Use requestIdleCallback to defer processing (non-blocking)
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(processImage, { timeout: 2000 });
      return () => {
        mounted.current = false;
        cancelIdleCallback(id);
      };
    } else {
      // Fallback to setTimeout
      const timeoutId = setTimeout(processImage, 100);
      return () => {
        mounted.current = false;
        clearTimeout(timeoutId);
      };
    }
  }, [src, removeTolerance]);

  return (
    <img
      ref={imgRef}
      src={processedSrc}
      alt={alt}
      width={imageWidth}
      height={imageHeight}
      loading="eager"
      decoding="async"
      // fetchpriority="high" is set via useEffect to avoid React warning
      {...rest}
    />
  );
}
